<template>
    <!-- Broadcast Modal -->
    <div v-if="showBroadcastModal" class="broadcast-modal-overlay" @click.self="dismissModal">
        <div class="broadcast-modal">
            <!-- Modal Header -->
            <div class="modal-header">
                <div class="header-icon">
                    <i class="bi bi-megaphone-fill"></i>
                </div>
                <div class="header-content">
                    <h3 class="modal-title">Admin Broadcast</h3>
                    <p class="modal-subtitle">Important Message from Control Panel</p>
                </div>
                <button @click="dismissModal" class="close-btn">
                    <i class="bi bi-x-lg"></i>
                </button>
            </div>

            <!-- Modal Body -->
            <div class="modal-body">
                <div class="message-container">
                    <div class="message-icon">
                        <i class="bi bi-broadcast"></i>
                    </div>
                    <div class="message-content">
                        <p class="broadcast-message">{{ broadcastMessage }}</p>
                        <div class="message-meta">
                            <span class="timestamp">{{ formatTimestamp(broadcastTimestamp) }}</span>
                            <span class="priority" :class="broadcastPriority">
                                {{ broadcastPriority }}
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Urgent Broadcast Effects -->
                <div v-if="isUrgent" class="urgent-indicator">
                    <div class="pulse-ring"></div>
                    <div class="pulse-ring delay-1"></div>
                    <div class="pulse-ring delay-2"></div>
                    <i class="bi bi-exclamation-triangle-fill"></i>
                </div>
            </div>

            <!-- Modal Footer -->
            <div class="modal-footer">
                <button @click="dismissModal" class="acknowledge-btn">
                    <i class="bi bi-check-circle-fill"></i>
                    Acknowledge
                </button>
                <button v-if="hasAction" @click="handleAction" class="action-btn">
                    <i class="bi bi-arrow-right-circle-fill"></i>
                    Take Action
                </button>
            </div>

            <!-- Decorative Elements -->
            <div class="modal-glow"></div>
            <div class="corner-decoration top-left"></div>
            <div class="corner-decoration top-right"></div>
            <div class="corner-decoration bottom-left"></div>
            <div class="corner-decoration bottom-right"></div>
        </div>

        <!-- Background Blur Effect -->
        <div class="modal-backdrop"></div>
    </div>
</template>

<script>
import { computed } from 'vue';

export default {
    name: 'BroadcastModal',
    props: {
        showBroadcastModal: Boolean,
        broadcastMessage: String,
        broadcastTimestamp: [String, Date],
        broadcastPriority: {
            type: String,
            default: 'normal',
            validator: (value) => ['low', 'normal', 'high', 'urgent'].includes(value)
        },
        hasAction: {
            type: Boolean,
            default: false
        }
    },
    setup(props, { emit }) {
        const isUrgent = computed(() => props.broadcastPriority === 'urgent');
        const dismissModal = () => {
            emit('dismiss');
        };

        const handleAction = () => {
            emit('action');
        };

        const formatTimestamp = (timestamp) => {
            if (!timestamp) return 'Just now';
            const date = new Date(timestamp);
            return date.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            });
        };

        // Auto-dismiss for non-urgent messages after 8 seconds
        if (props.showBroadcastModal && !isUrgent.value) {
            setTimeout(() => {
                if (props.showBroadcastModal) {
                    dismissModal();
                }
            }, 10000);
        }

       
        return {
            isUrgent,
            dismissModal,
            handleAction,
            formatTimestamp
        };
    }
};
</script>

<style scoped>

/* Modal Overlay */
.broadcast-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 20px;
}

.modal-backdrop {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(10px);
    z-index: -1;
}

/* Main Modal */
.broadcast-modal {
    position: relative;
    background: linear-gradient(135deg, var(--card-bg) 0%, #222 100%);
    border: 2px solid var(--border-gold);
    border-radius: 20px;
    width: 100%;
    max-width: 500px;
    box-shadow:
        0 25px 50px rgba(0, 0, 0, 0.5),
        0 0 0 1px rgba(212, 175, 55, 0.1),
        0 0 60px rgba(212, 175, 55, 0.2);
    overflow: hidden;
    animation: modalAppear 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    z-index: 10000;
}

.modal-glow {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at 50% 0%, rgba(212, 175, 55, 0.1) 0%, transparent 50%);
    pointer-events: none;
}

/* Modal Header */
.modal-header {
    display: flex;
    align-items: center;
    padding: 25px 30px 20px;
    border-bottom: 1px solid var(--border-gold);
    background: linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, transparent 100%);
    position: relative;
}

.header-icon {
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, var(--gold-primary) 0%, var(--gold-secondary) 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 15px;
    box-shadow: 0 0 20px var(--shadow-gold);
}

.header-icon i {
    color: var(--dark-bg);
    font-size: 1.3rem;
}

.header-content {
    flex: 1;
}

.modal-title {
    font-size: 1.5rem;
    font-weight: 700;
    background: linear-gradient(135deg, var(--text-gold) 0%, var(--gold-light) 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    margin: 0 0 5px 0;
}

.modal-subtitle {
    color: var(--text-muted);
    font-size: 0.9rem;
    margin: 0;
    font-weight: 300;
}

.close-btn {
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid var(--border-gold);
    border-radius: 8px;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-muted);
    cursor: pointer;
    transition: all 0.3s ease;
}

.close-btn:hover {
    background: rgba(212, 175, 55, 0.1);
    color: var(--gold-primary);
    border-color: var(--gold-primary);
    transform: rotate(90deg);
}

/* Modal Body */
.modal-body {
    padding: 30px;
    position: relative;
}

.message-container {
    display: flex;
    align-items: flex-start;
    gap: 20px;
}

.message-icon {
    width: 60px;
    height: 60px;
    background: rgba(212, 175, 55, 0.1);
    border: 2px solid var(--border-gold);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.message-icon i {
    color: var(--gold-primary);
    font-size: 1.5rem;
}

.message-content {
    flex: 1;
}

.broadcast-message {
    color: var(--text-light);
    font-size: 1.1rem;
    line-height: 1.6;
    margin: 0 0 15px 0;
    font-weight: 400;
}

.message-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
}

.timestamp {
    color: var(--text-muted);
    font-size: 0.85rem;
    font-weight: 300;
}

.priority {
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.priority.low {
    background: rgba(46, 204, 113, 0.2);
    color: var(--low-green);
    border: 1px solid rgba(46, 204, 113, 0.3);
}

.priority.normal {
    background: rgba(52, 152, 219, 0.2);
    color: var(--normal-blue);
    border: 1px solid rgba(52, 152, 219, 0.3);
}

.priority.high {
    background: rgba(243, 156, 18, 0.2);
    color: var(--high-orange);
    border: 1px solid rgba(243, 156, 18, 0.3);
}

.priority.urgent {
    background: rgba(231, 76, 60, 0.2);
    color: var(--urgent-red);
    border: 1px solid rgba(231, 76, 60, 0.3);
    animation: pulseUrgent 2s infinite;
}

/* Urgent Indicator */
.urgent-indicator {
    position: absolute;
    top: 20px;
    right: 20px;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.urgent-indicator i {
    color: var(--urgent-red);
    font-size: 1.2rem;
    position: relative;
    z-index: 2;
    animation: bounce 1s infinite;
}

.pulse-ring {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 2px solid var(--urgent-red);
    border-radius: 50%;
    animation: pulseRing 2s linear infinite;
}

.pulse-ring.delay-1 {
    animation-delay: 0.66s;
}

.pulse-ring.delay-2 {
    animation-delay: 1.33s;
}

/* Modal Footer */
.modal-footer {
    padding: 20px 30px 25px;
    border-top: 1px solid var(--border-gold);
    display: flex;
    gap: 15px;
    background: rgba(0, 0, 0, 0.2);
}

.acknowledge-btn,
.action-btn {
    flex: 1;
    padding: 12px 20px;
    border: none;
    border-radius: 10px;
    font-weight: 600;
    font-size: 0.95rem;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.acknowledge-btn {
    background: rgba(212, 175, 55, 0.1);
    border: 2px solid var(--border-gold);
    color: var(--text-light);
}

.acknowledge-btn:hover {
    background: rgba(212, 175, 55, 0.2);
    border-color: var(--gold-primary);
    color: var(--gold-primary);
    transform: translateY(-2px);
}

.action-btn {
    background: linear-gradient(135deg, var(--gold-primary) 0%, var(--gold-secondary) 100%);
    border: 2px solid transparent;
    color: var(--dark-bg);
}

.action-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(212, 175, 55, 0.4);
}

/* Corner Decorations */
.corner-decoration {
    position: absolute;
    width: 20px;
    height: 20px;
    border: 2px solid transparent;
    opacity: 0.5;
}

.corner-decoration.top-left {
    top: 10px;
    left: 10px;
    border-top: 2px solid var(--gold-primary);
    border-left: 2px solid var(--gold-primary);
    border-top-left-radius: 8px;
}

.corner-decoration.top-right {
    top: 10px;
    right: 10px;
    border-top: 2px solid var(--gold-primary);
    border-right: 2px solid var(--gold-primary);
    border-top-right-radius: 8px;
}

.corner-decoration.bottom-left {
    bottom: 10px;
    left: 10px;
    border-bottom: 2px solid var(--gold-primary);
    border-left: 2px solid var(--gold-primary);
    border-bottom-left-radius: 8px;
}

.corner-decoration.bottom-right {
    bottom: 10px;
    right: 10px;
    border-bottom: 2px solid var(--gold-primary);
    border-right: 2px solid var(--gold-primary);
    border-bottom-right-radius: 8px;
}

/* Animations */
@keyframes modalAppear {
    0% {
        opacity: 0;
        transform: scale(0.8) translateY(50px);
    }

    100% {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
}

@keyframes pulseRing {
    0% {
        transform: scale(0.8);
        opacity: 1;
    }

    100% {
        transform: scale(2);
        opacity: 0;
    }
}

@keyframes pulseUrgent {

    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0.7;
    }
}

@keyframes bounce {

    0%,
    100% {
        transform: scale(1);
    }

    50% {
        transform: scale(1.2);
    }
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
    .broadcast-modal-overlay {
        padding: 15px;
    }

    .modal-header {
        padding: 20px 25px 15px;
    }

    .modal-body {
        padding: 25px;
    }

    .message-container {
        flex-direction: column;
        text-align: center;
        gap: 15px;
    }

    .message-meta {
        flex-direction: column;
        gap: 10px;
    }

    .modal-footer {
        padding: 15px 25px 20px;
        flex-direction: column;
    }

    .urgent-indicator {
        top: 15px;
        right: 15px;
        width: 35px;
        height: 35px;
    }
}

@media (max-width: 480px) {
    .modal-header {
        padding: 15px 20px 10px;
    }

    .modal-body {
        padding: 20px;
    }

    .modal-footer {
        padding: 15px 20px;
    }

    .header-icon {
        width: 40px;
        height: 40px;
        margin-right: 10px;
    }

    .header-icon i {
        font-size: 1.1rem;
    }

    .modal-title {
        font-size: 1.3rem;
    }

    .broadcast-message {
        font-size: 1rem;
    }
}
</style>